import { useCallback, useMemo, useState } from "react";
import { Chess } from "chess.js";

/**
 * useClickToMove — shared click-to-move + legal-move-dot logic for any board.
 *
 * @param {Chess} chess     — a chess.js instance representing the current position
 * @param {(from, to, piece, isCapture) => boolean} onMoveAttempted
 *                          — called when the user attempts a move (click OR drag);
 *                            return true if the move was accepted by the page.
 * @param {boolean} enabled — false disables interaction (e.g., during animations)
 *
 * Returns:
 *   { onSquareClick, onPieceDrop, squareStyles, selected }
 *
 * squareStyles combines:
 *  - a brass ring on the selected square
 *  - a small centered dot on every legal target square
 *  - a subtle brass halo on legal capture squares
 */
export function useClickToMove(chess, onMoveAttempted, enabled = true) {
  const [selected, setSelected] = useState(null);

  const clear = useCallback(() => setSelected(null), []);

  const legalTargets = useMemo(() => {
    if (!enabled || !selected || !chess) return [];
    try {
      return chess.moves({ square: selected, verbose: true });
    } catch {
      return [];
    }
  }, [chess, selected, enabled]);

  const legalTargetSet = useMemo(() => new Set(legalTargets.map((m) => m.to)), [legalTargets]);
  const captureSet = useMemo(
    () => new Set(legalTargets.filter((m) => m.captured || m.flags?.includes("e")).map((m) => m.to)),
    [legalTargets],
  );

  const squareStyles = useMemo(() => {
    if (!enabled) return {};
    const styles = {};
    if (selected) {
      styles[selected] = {
        background: "rgba(198,161,91,0.35)",
        boxShadow: "inset 0 0 0 3px rgba(198,161,91,0.9)",
      };
    }
    for (const sq of legalTargetSet) {
      if (captureSet.has(sq)) {
        // ring on captures
        styles[sq] = {
          background:
            "radial-gradient(circle, transparent 58%, rgba(198,161,91,0.55) 58%, rgba(198,161,91,0.55) 66%, transparent 66%)",
        };
      } else {
        // solid centered dot on quiet moves
        styles[sq] = {
          background:
            "radial-gradient(circle, rgba(198,161,91,0.55) 0%, rgba(198,161,91,0.55) 22%, transparent 22%)",
        };
      }
    }
    return styles;
  }, [selected, legalTargetSet, captureSet, enabled]);

  const attemptMove = useCallback(
    (from, to) => {
      if (!enabled || !chess) return false;
      try {
        // detect promotion & capture on a *copy*
        const test = new Chess(chess.fen());
        const legal = test.moves({ square: from, verbose: true }).find((m) => m.to === to);
        if (!legal) return false;
        const promo = legal.promotion || (legal.piece === "p" && (to[1] === "1" || to[1] === "8") ? "q" : undefined);
        const isCapture = !!legal.captured;
        const piece = (legal.color + legal.piece.toUpperCase());
        const ok = onMoveAttempted?.(from, to, piece, isCapture, promo);
        if (ok !== false) clear();
        return ok !== false;
      } catch {
        return false;
      }
    },
    [chess, onMoveAttempted, clear, enabled],
  );

  const onSquareClick = useCallback(
    ({ square, piece }) => {
      if (!enabled || !chess) return;
      // clicking currently selected square → deselect
      if (selected === square) {
        clear();
        return;
      }
      // if a piece is selected, try to move there
      if (selected) {
        const ok = attemptMove(selected, square);
        if (ok) return;
        // if user clicks their own piece: re-select
        const turn = chess.turn();
        if (piece?.pieceType && piece.pieceType[0]?.toLowerCase() === turn) {
          setSelected(square);
        } else {
          clear();
        }
        return;
      }
      // no selection yet → select if there's a piece of the side to move
      if (piece?.pieceType) {
        const turn = chess.turn();
        if (piece.pieceType[0]?.toLowerCase() === turn) {
          setSelected(square);
        }
      }
    },
    [selected, chess, attemptMove, clear, enabled],
  );

  const onPieceDrop = useCallback(
    (from, to /*, pieceStr */) => {
      if (!enabled) return false;
      const ok = attemptMove(from, to);
      return !!ok;
    },
    [attemptMove, enabled],
  );

  return { onSquareClick, onPieceDrop, squareStyles, selected, clearSelection: clear };
}
